import { useMemo } from 'react'
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { DailyDashboardStatDto } from '@api/types/dashboard'
import { CATEGORY_NAMES, CATEGORY_COLORS, CHART_COLORS } from './chartConfig'

interface CategoryBreakdownChartProps {
  data?: DailyDashboardStatDto[]
  isLoading?: boolean
}

export function CategoryBreakdownChart({ data, isLoading }: CategoryBreakdownChartProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // Get all unique categories from the data
  const categories = useMemo(() => {
    if (!data) return []
    const categorySet = new Set<number>()
    data.forEach((day) => {
      day.completionsByCategory?.forEach((cat) => {
        categorySet.add(cat.category)
      })
    })
    return Array.from(categorySet).sort((a, b) => a - b)
  }, [data])

  const chartData = useMemo(() => {
    if (!data) return []
    return data.map((item) => {
      const dataPoint: Record<string, number | string> = {
        date: new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
      }

      // Initialize all categories to 0
      categories.forEach((catId) => {
        dataPoint[`category_${catId}`] = 0
      })

      // Fill in actual values (total completions = solo + group for each category)
      item.completionsByCategory?.forEach((cat) => {
        dataPoint[`category_${cat.category}`] = cat.soloCompletions + cat.groupCompletions
      })

      return dataPoint
    })
  }, [data, categories])

  const gridColor = isDark ? CHART_COLORS.gridDark : CHART_COLORS.grid
  const axisColor = isDark ? CHART_COLORS.axisDark : CHART_COLORS.axis

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Completions by Category
      </Typography>
      {isLoading ? (
        <Box sx={{ height: 300 }}>
          <Skeleton variant="rectangular" height="100%" />
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={{ fill: axisColor, fontSize: 12 }} />
            <YAxis tick={{ fill: axisColor, fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend />
            {categories.map((catId) => (
              <Area
                key={catId}
                type="monotone"
                dataKey={`category_${catId}`}
                name={CATEGORY_NAMES[catId] || `Category ${catId}`}
                stackId="1"
                stroke={CATEGORY_COLORS[catId] || '#666666'}
                fill={CATEGORY_COLORS[catId] || '#666666'}
                fillOpacity={0.6}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Paper>
  )
}
